import React, { useState } from "react";
import "./PolicySection.css";
import { TERMS_HTML_GB } from "../Auth/Terms-en-GB";
import { DPA_HTML_GB } from "../Auth/GDPR-en-GB";
import { TERMS_HTML_ES } from "../Auth/Terms-es";
import { DPA_HTML_ES } from "../Auth/GDPR-es";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

// --- Pull your existing HTML strings (env-aware) ---
const PRODUCT_NAME = process.env.REACT_APP_PRODUCT_NAME ?? "Your Product";
const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME ?? "Your Company";
const COMPANY_ADDR = process.env.REACT_APP_COMPANY_REGISTERED_ADDRESS ?? "Your Registered Address";
const COMPANY_NO = process.env.REACT_APP_COMPANY_REGISTERED_NUMBER ?? "00000000";
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL ?? "support@example.com";

// Versions (match your DPA effective date)
const TERMS_VERSION = "2025-11-01";
const DPA_VERSION = "2025-11-01";

// Helpers
function downloadHtml(filename, htmlString) {
  const blob = new Blob([htmlString], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ❗️Leave printHtml as-is (no translation inside)
function printHtml(title, htmlString) {
  const w = window.open("", "_blank", "noopener,noreferrer,width=980");
  if (!w) return;
  w.document.open();
  w.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>
  body{font-family:Inter,system-ui,Arial,sans-serif;line-height:1.6;padding:24px;max-width:900px;margin:0 auto;}
  h2{margin-top:0}
  h4{margin:1.25rem 0 .5rem;font-weight:600}
  ul{padding-left:1.25rem}
  code{background:#f3f4f6;padding:.1rem .25rem;border-radius:4px}
  hr{border:0;border-top:1px solid #eee;margin:1.25rem 0}
</style>
</head>
<body>${htmlString}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 150);
}

export default function PoliciesPage({
  config,
  orgAcceptedAt, // ISO string e.g. "2025-11-03T10:22:00Z"
  orgAcceptedBy, // e.g. "alice@business.com"
}) {
  const { t, i18n } = useTranslation("policy");
  const [openModal, setOpenModal] = useState(null); // "terms" | "dpa" | null
  const dateObj =
  orgAcceptedAt instanceof Date ? orgAcceptedAt : new Date(orgAcceptedAt);
  const friendlyDate = isNaN(dateObj)
    ? String(orgAcceptedAt)
    : new Intl.DateTimeFormat(i18n.language || "en", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      }).format(dateObj).replace(/,/g, "");

  const acceptedText = orgAcceptedAt
    ? friendlyDate
    : "—";


  const friendlyDPADate = isNaN(new Date(2025, 10, 1))
    ? String(new Date(2025, 10, 1))
    : new Intl.DateTimeFormat(i18n.language || "en", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric"
      }).format(new Date(2025, 10, 1)).replace(/,/g, "");


  return (
    <div className="policies-root">
      {/* <h1>{t("title")}</h1> */}

      {/* Terms & Conditions */}
      <section className="policy-card">
        <header className="policy-card__header">
          <div>
            <h2>{t("terms.heading")}</h2>
            <div className="policy-meta">
              {t("common.version")}: {TERMS_VERSION} • {t("common.accepted")}: {acceptedText}
              {orgAcceptedBy ? ` • ${t("common.by")} ${orgAcceptedBy}` : ""}
            </div>
          </div>
          <div className="policy-actions">
            <button
              style={{ border: "none" }}
              className="btn-pol"
              onClick={() => setOpenModal("terms")}
            >
              {t("common.view")}
            </button>
            {/* <button className="btn" onClick={() => printHtml(`${COMPANY_NAME} – Terms`, TERMS_HTML)}>
              {t("common.print")}
            </button> */}
            <button
              className="btn-pol btn-outline"
              onClick={() => downloadHtml("Terms-and-Conditions.html", config.systemLanguage === "en-GB" ? TERMS_HTML_GB : config.systemLanguage === "es" ? TERMS_HTML_ES : TERMS_HTML_GB)}
            >
              {t("common.download")}
            </button>
          </div>
        </header>
        <details className="policy-accordion">
          <summary>{t("common.preview")}</summary>
          <div className="policy-preview" dangerouslySetInnerHTML={{ __html: config.systemLanguage === "en-GB" ? TERMS_HTML_GB : config.systemLanguage === "es" ? TERMS_HTML_ES : TERMS_HTML_GB }} />
        </details>
      </section>

      {/* DPA */}
      <section className="policy-card">
        <header className="policy-card__header">
          <div>
            <h2>{t("dpa.heading")}</h2>
            <div className="policy-meta">
              {t("common.version")}: {DPA_VERSION} • {t("dpa.effective")} {friendlyDPADate}
            </div>
          </div>
          <div className="policy-actions">
            <button
              style={{ border: "none" }}
              className="btn-pol"
              onClick={() => setOpenModal("dpa")}
            >
              {t("common.view")}
            </button>
            {/* <button className="btn" onClick={() => printHtml(`${COMPANY_NAME} – DPA`, DPA_HTML)}>
              {t("common.print")}
            </button> */}
            <button
              className="btn-pol btn-outline"
              onClick={() => downloadHtml("Data-Processing-Addendum.html", config.systemLanguage === "en-GB" ? DPA_HTML_GB : config.systemLanguage === "es" ? DPA_HTML_ES : DPA_HTML_GB)}
            >
              {t("common.download")}
            </button>
          </div>
        </header>
        <details className="policy-accordion">
          <summary>{t("common.preview")}</summary>
          <div className="policy-preview" dangerouslySetInnerHTML={{ __html: config.systemLanguage === "en-GB" ? DPA_HTML_GB : config.systemLanguage === "es" ? DPA_HTML_ES : DPA_HTML_GB }} />
        </details>
      </section>

      {/* Modal */}
      {openModal && (
        <div
          className="policy-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpenModal(null)}
        >
          <div className="policy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="policy-modal__header">
              <h3>
                {openModal === "terms" ? t("terms.heading") : t("dpa.heading")}
              </h3>
              <Button
                variant=""
                type="button"
                className="btn-close"
                data-dismiss="modal"
                onClick={() => setOpenModal(false)}
              />
            </div>
            <div
              className="policy-modal__body"
              dangerouslySetInnerHTML={{ __html: openModal === "terms" ? (config.systemLanguage === "en-GB" ? TERMS_HTML_GB : config.systemLanguage === "es" ? TERMS_HTML_ES : TERMS_HTML_GB) : (config.systemLanguage === "en-GB" ? DPA_HTML_GB : config.systemLanguage === "es" ? DPA_HTML_ES : DPA_HTML_GB) }}
            />
            <div className="policy-modal__actions">
              {/* <button
                className="btn"
                onClick={() =>
                  printHtml(
                    openModal === "terms" ? `${COMPANY_NAME} – Terms` : `${COMPANY_NAME} – DPA`,
                    openModal === "terms" ? TERMS_HTML : DPA_HTML
                  )
                }
              >
                {t("common.print")}
              </button> */}
              <button
                className="btn-pol btn-outline"
                onClick={() =>
                  downloadHtml(
                    openModal === "terms" ? "Terms-and-Conditions.html" : "Data-Processing-Addendum.html",
                    openModal === "terms" ? (config.systemLanguage === "en-GB" ? TERMS_HTML_GB : config.systemLanguage === "es" ? TERMS_HTML_ES : TERMS_HTML_GB) : (config.systemLanguage === "en-GB" ? DPA_HTML_GB : config.systemLanguage === "es" ? DPA_HTML_ES : DPA_HTML_GB)
                  )
                }
              >
                {t("common.download")}
              </button>
              <button className="btn-neutral" onClick={() => setOpenModal(null)}>
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="policy-footnote">
        {t("footnote.needCopy")}{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    </div>
  );
}
